import { useState, useMemo } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2, Eye, EyeOff, Check, X } from "lucide-react"
import { authApi } from '@/shared/services/auth'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

// Contraseñas comunes a evitar
const COMMON_PASSWORDS = [
  "password", "123456", "12345678", "qwerty", "abc123", "monkey", "1234567",
  "letmein", "trustno1", "dragon", "baseball", "iloveyou", "master", "sunshine",
  "ashley", "bailey", "shadow", "123123", "654321", "superman", "qazwsx",
  "michael", "football", "password1", "password123", "welcome", "jesus", "ninja",
  "mustang", "password2", "amanda", "jessica", "lovely", "12345", "123456789",
]

interface PasswordValidation {
  minLength: boolean
  notCommon: boolean
  notOnlyNumeric: boolean
}

function validatePassword(password: string): PasswordValidation {
  return {
    minLength: password.length >= 8,
    notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    notOnlyNumeric: !/^\d+$/.test(password),
  }
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
  onSuccess,
}: ChangePasswordDialogProps) {
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword1, setNewPassword1] = useState("")
  const [newPassword2, setNewPassword2] = useState("")
  const [passwordMismatch, setPasswordMismatch] = useState(false)
  const [showOld, setShowOld] = useState(false)
  const [showNew1, setShowNew1] = useState(false)
  const [showNew2, setShowNew2] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [successMessage, setSuccessMessage] = useState("")

  const passwordValidation = useMemo(() => validatePassword(newPassword1), [newPassword1])
  const isPasswordValid = newPassword1 === "" || (
    passwordValidation.minLength &&
    passwordValidation.notCommon &&
    passwordValidation.notOnlyNumeric
  )

  const resetForm = () => {
    setOldPassword("")
    setNewPassword1("")
    setNewPassword2("")
    setPasswordMismatch(false)
    setErrors({})
    setSuccessMessage("")
  }

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm()
    }
    onOpenChange(open)
  }

  const handleSubmit = async () => {
    setErrors({})
    setSuccessMessage("")

    // Client-side validation
    const newErrors: Record<string, string> = {}
    if (!oldPassword) newErrors.old_password = "Ingresa tu contraseña actual"
    if (!newPassword1) newErrors.new_password1 = "Ingresa la nueva contraseña"
    if (!newPassword2) newErrors.new_password2 = "Confirma la nueva contraseña"
    if (newPassword1 && newPassword2 && newPassword1 !== newPassword2) {
      newErrors.new_password2 = "Las contraseñas no coinciden"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)

    try {
      const response = await authApi.changePassword({
        old_password: oldPassword,
        new_password1: newPassword1,
        new_password2: newPassword2,
      })

      if (response.success) {
        setSuccessMessage(response.message)
        setTimeout(() => {
          handleClose(false)
          onSuccess()
        }, 1500)
      } else {
        if (response.errors) {
          setErrors(response.errors)
        } else {
          setErrors({ general: response.message })
        }
      }
    } catch {
      setErrors({ general: "Error de conexión. Inténtalo de nuevo." })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
          <DialogDescription>
            Ingresa tu contraseña actual y la nueva contraseña.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="old_password">Contraseña actual</Label>
            <div className="relative">
              <Input
                id="old_password"
                type={showOld ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Ingresa tu contraseña actual"
                className={errors.old_password ? "border-destructive" : ""}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowOld(!showOld)}
              >
                {showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.old_password && (
              <p className="text-xs text-destructive">{errors.old_password}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="new_password1">Nueva contraseña</Label>
            <div className="relative">
              <Input
                id="new_password1"
                type={showNew1 ? "text" : "password"}
                value={newPassword1}
                onChange={(e) => {
                  const value = e.target.value
                  setNewPassword1(value)
                  setPasswordMismatch(newPassword2 !== "" && value !== "" && value !== newPassword2)
                }}
                placeholder="Ingresa una nueva contraseña"
                className={errors.new_password1 || (!isPasswordValid && newPassword1) ? "border-destructive" : ""}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowNew1(!showNew1)}
              >
                {showNew1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {newPassword1 && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  {passwordValidation.minLength ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <X className="h-3 w-3 text-destructive" />
                  )}
                  <span className={passwordValidation.minLength ? "text-green-600" : "text-destructive"}>
                    Mínimo 8 caracteres
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {passwordValidation.notOnlyNumeric ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <X className="h-3 w-3 text-destructive" />
                  )}
                  <span className={passwordValidation.notOnlyNumeric ? "text-green-600" : "text-destructive"}>
                    No puede ser solo números
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {passwordValidation.notCommon ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <X className="h-3 w-3 text-destructive" />
                  )}
                  <span className={passwordValidation.notCommon ? "text-green-600" : "text-destructive"}>
                    No puede ser una contraseña común
                  </span>
                </div>
              </div>
            )}
            {errors.new_password1 && (
              <p className="text-xs text-destructive">{errors.new_password1}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="new_password2">Confirmar nueva contraseña</Label>
            <div className="relative">
              <Input
                id="new_password2"
                type={showNew2 ? "text" : "password"}
                value={newPassword2}
                onChange={(e) => {
                  const value = e.target.value
                  setNewPassword2(value)
                  setPasswordMismatch(newPassword1 !== "" && value !== "" && newPassword1 !== value)
                }}
                placeholder="Confirma tu nueva contraseña"
                className={errors.new_password2 || passwordMismatch ? "border-destructive" : ""}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowNew2(!showNew2)}
              >
                {showNew2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {passwordMismatch && !errors.new_password2 && (
              <p className="text-xs text-destructive">Las contraseñas no coinciden</p>
            )}
            {errors.new_password2 && (
              <p className="text-xs text-destructive">{errors.new_password2}</p>
            )}
          </div>

          {errors.general && (
            <div className="rounded-md bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{errors.general}</p>
            </div>
          )}

          {successMessage && (
            <div className="rounded-md bg-green-50 p-3">
              <p className="text-sm text-green-600">{successMessage}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || passwordMismatch || !oldPassword || !newPassword1 || !newPassword2 || !isPasswordValid}
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cambiar Contraseña
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
